/**
 * Workspace utilities — prepare session directories and write skaile.yaml.
 */

import { existsSync, mkdirSync, mkdtempSync, rmSync, writeFileSync } from "node:fs";
import { tmpdir } from "node:os";
import { join } from "node:path";
import { stringify } from "yaml";
import type { CreateAgentSessionOptions } from "./types.js";

/**
 * Prepare a workspace directory for a session. Returns the path and cleanup fn.
 *
 * If `opts.workspaceDir` is set, that directory is created (if needed) and returned
 * with no cleanup. Otherwise a unique temporary directory (`skaile-sdk-*` under the
 * OS temp dir) is created via `mkdtempSync`; the cleanup function removes it on
 * session stop unless `keepWorkspace` is true.
 *
 * @param opts - Session creation options providing `workspaceDir` and `keepWorkspace`.
 * @returns An object with the resolved `dir` path and an optional `cleanup` function.
 * @docLink packages/sdk/concepts#workspace-helpers
 */
export function prepareWorkspace(opts: CreateAgentSessionOptions): {
  dir: string;
  cleanup: (() => void) | undefined;
} {
  if (opts.workspaceDir) {
    mkdirSync(opts.workspaceDir, { recursive: true });
    return { dir: opts.workspaceDir, cleanup: undefined };
  }

  // mkdtemp yields an unpredictable, collision-free name (vs. a guessable
  // timestamp path) — avoids cross-session clobbering and symlink races in /tmp.
  const dir = mkdtempSync(join(tmpdir(), "skaile-sdk-"));

  const cleanup = opts.keepWorkspace
    ? undefined
    : () => rmSync(dir, { recursive: true, force: true });

  return { dir, cleanup };
}

/**
 * Write skaile.yaml from inline {@link CreateAgentSessionOptions}.
 *
 * Only writes if `agent`, `mounts`, or `connectors` are provided and no `skaile.yaml`
 * already exists in `dir` (existing config takes precedence). Connector `adapter` fields
 * are mapped to the `driver` field expected by the core runtime.
 *
 * @param dir - Absolute path to the workspace directory.
 * @param opts - Session options containing agent config, mounts, and connectors to serialize.
 * @docLink packages/sdk/concepts#workspace-helpers
 */
export function writeSkaileYaml(dir: string, opts: CreateAgentSessionOptions): void {
  if (!opts.agent && !opts.mounts?.length && !opts.connectors?.length) return;
  const dest = join(dir, "skaile.yaml");
  if (existsSync(dest)) return; // existing config wins

  const cfg: Record<string, unknown> = {
    name: "sdk-session",
  };

  if (opts.agent) {
    cfg["agent-config"] = {
      default: {
        ...(opts.agent.driver && { driver: opts.agent.driver }),
        ...(opts.agent.provider && { provider: opts.agent.provider }),
        ...(opts.agent.model && { model: opts.agent.model }),
      },
    };

    if (opts.agent.maxTurns !== undefined || opts.agent.permissionMode !== undefined) {
      cfg.agent = {
        permissions: {
          ...(opts.agent.maxTurns !== undefined && { max_turns: opts.agent.maxTurns }),
          ...(opts.agent.permissionMode !== undefined && {
            permission_mode: opts.agent.permissionMode,
          }),
        },
      };
    }
  }

  if (opts.mounts?.length) {
    cfg.mounts = opts.mounts;
  }

  if (opts.connectors?.length) {
    // Map SDK field names to core's ConnectorDeclaration field names:
    // SDK uses `adapter` → core expects `driver`
    cfg.connectors = opts.connectors.map((c) => ({
      id: c.id,
      driver: c.adapter,
      ...(c.access && { access: c.access }),
      ...(c.auth && { auth: c.auth }),
      ...(c.options && { options: c.options }),
    }));
  }

  writeFileSync(dest, stringify(cfg));
}
